import { Button, IconButton, Menu, Typography } from '@neo4j-ndl/react';
import {
  ArrowPathIconOutline,
  ChevronDownIconOutline,
  ChevronUpIconOutline,
  EllipsisHorizontalIconSolid,
} from '@neo4j-ndl/react/icons';
import { MODAL_TYPE, useModal } from '@nx/state';
import type { DbInfo, DbmsInfo } from '@nx/state/desktop';
import { DBMS_STATUS, useDropDatabaseMutation, useListDatabasesQuery } from '@nx/state/desktop';
import { isNotNullish } from '@nx/stdlib';
import { DataGridHelpers } from '@nx/ui';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import cn from 'classnames';
import { memo, useMemo, useRef, useState } from 'react';

function DatabaseActionsComponent({
  databaseName,
  instanceId,
  instanceStatus,
}: {
  databaseName: string;
  instanceId: string;
  instanceStatus: DBMS_STATUS;
}) {
  const [openMenu, setOpenMenu] = useState(false);
  const [dropDatabase] = useDropDatabaseMutation();
  const actionsButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="flex w-full items-center justify-end gap-1">
      <IconButton isClean ref={actionsButtonRef} ariaLabel="Open menu" size="small" onClick={() => setOpenMenu(true)}>
        <EllipsisHorizontalIconSolid />
      </IconButton>
      <Menu isOpen={openMenu} onClose={() => setOpenMenu(false)} anchorRef={actionsButtonRef}>
        <Menu.Items>
          <Menu.Item
            isDisabled={instanceStatus !== DBMS_STATUS.STARTED}
            title="Delete"
            onClick={() => {
              void dropDatabase({ dbmsId: instanceId, database: databaseName });
              setOpenMenu(false);
            }}
          />
        </Menu.Items>
      </Menu>
    </div>
  );
}

const DatabaseActions = memo(DatabaseActionsComponent);

function DatabaseListComponent({ instance }: { instance: DbmsInfo }) {
  const { data: databases = [], refetch, isFetching } = useListDatabasesQuery(instance.id);
  const createDatabaseModal = useModal(MODAL_TYPE.DBMS_DB_CREATE);

  const columnHelper = createColumnHelper<DbInfo>();
  const [isOpen, toggleOpen] = useState(false);

  const columns = useMemo(
    () => [
      columnHelper.accessor('databaseName', { header: 'Name' }),

      columnHelper.display({
        id: 'actions',
        cell: (cx) => {
          const database = cx.row.original;
          if (database.databaseName === 'system') {
            return null;
          }
          return (
            <DatabaseActions
              databaseName={database.databaseName}
              instanceId={instance.id}
              instanceStatus={instance.status}
            />
          );
        },
      }),
    ],
    [columnHelper, instance],
  );

  const table = useReactTable({
    columns,
    data: databases,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: {
        right: ['actions'],
      },
    },
  });

  // Turns out metadata can actually be undefined somehow
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const { lastDatabasesRefresh = null } = instance.metadata ?? {};

  return (
    <details className="bg-palette-neutral-bg-on-bg-weak rounded-lg">
      <summary
        className="hover:bg-palette-neutral-hover flex cursor-pointer items-center gap-3 p-3"
        onClick={() => toggleOpen(!isOpen)}
      >
        {isOpen ? <ChevronUpIconOutline className="h-4" /> : <ChevronDownIconOutline className="h-4" />}
        <Typography variant="subheading-small">Databases ({databases.length})</Typography>
        <div className="ml-auto flex items-center gap-3">
          {isNotNullish(lastDatabasesRefresh) && typeof lastDatabasesRefresh === 'number' && (
            <Typography variant="subheading-small">
              {isFetching
                ? 'Refreshing…'
                : !(instance.status === DBMS_STATUS.STOPPED && databases.length === 0) &&
                  `Last updated: ${new Date(lastDatabasesRefresh).toLocaleString(navigator.language, {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}`}
            </Typography>
          )}
          <IconButton
            ariaLabel="Refresh databases"
            isClean
            isLoading={isFetching}
            isDisabled={instance.status !== DBMS_STATUS.STARTED}
            onClick={(e) => {
              e.stopPropagation();
              void refetch();
            }}
            size="small"
          >
            <ArrowPathIconOutline className={cn({ isFetching: 'animate-spin' })} />
          </IconButton>
          <Button
            fill="outlined"
            size="small"
            color="neutral"
            onClick={(e) => {
              e.stopPropagation();
              createDatabaseModal.open({ dbmsId: instance.id, databases });
            }}
            isDisabled={instance.status !== DBMS_STATUS.STARTED}
            htmlAttributes={{
              title: instance.status === DBMS_STATUS.STOPPED ? 'Start instance to create databases' : undefined,
            }}
          >
            Create database
          </Button>
        </div>
      </summary>

      {isOpen && (
        <div className="[&_.ndl-data-grid-td]:!bg-palette-neutral-bg-on-bg-weak rounded-b-lg p-3">
          <DataGridHelpers.DataGridRightColumnPinned
            tableInstance={table}
            components={{
              Navigation: null,
            }}
            isResizable={false}
          />
        </div>
      )}
    </details>
  );
}

export const DatabaseList = memo(DatabaseListComponent);
